import React from 'react';
import { Circle, G, Line, Path, Polygon, Rect, Text as SvgText } from 'react-native-svg';
import { DiagramCanvas } from '../DiagramCanvas';
import { FormulaTag } from '../FormulaTag';
import { LeaderLine } from '../LeaderLine';
import { DT } from '../tokens';

/** C_n ≤ ISO limit — particle counter probe + scattered particles + digital readout. */
export function DiagramCleanroomParticleConcentration() {
  // Particles scattered
  const particles: Array<[number, number, number]> = [
    [100, 90, 3], [140, 110, 2], [170, 85, 2.5], [200, 120, 2], [130, 145, 3.2],
    [175, 155, 2], [120, 175, 2.5], [160, 130, 2], [110, 160, 3], [185, 100, 2],
    [145, 180, 2], [95, 125, 2.5], [210, 170, 2], [155, 100, 2], [230, 150, 2],
  ];

  return (
    <DiagramCanvas>
      {/* Room */}
      <Rect x={60} y={60} width={200} height={150} fill={DT.fill} stroke={DT.stroke} strokeWidth="1.6" />
      <SvgText x={160} y={54} fill={DT.desc} fontSize="9" fontFamily={DT.mono} textAnchor="middle">Cleanroom (1 m³ sample)</SvgText>

      {/* Subtle grid */}
      {[0, 1, 2, 3].map(i => (
        <G key={i}>
          <Line x1={60} y1={90 + i * 30} x2={260} y2={90 + i * 30} stroke={DT.dim} strokeWidth="0.3" strokeDasharray="3,4" />
          <Line x1={110 + i * 40} y1={60} x2={110 + i * 40} y2={210} stroke={DT.dim} strokeWidth="0.3" strokeDasharray="3,4" />
        </G>
      ))}

      {/* Particles with slight glow */}
      {particles.map(([px, py, pr], i) => (
        <G key={i}>
          <Circle cx={px} cy={py} r={pr + 2} fill={DT.varColor} opacity={0.15} />
          <Circle cx={px} cy={py} r={pr} fill={DT.varColor} opacity={0.9} />
        </G>
      ))}

      {/* Sample probe (isokinetic) */}
      <Line x1={200} y1={135} x2={200} y2={100} stroke={DT.stroke} strokeWidth="2.4" />
      <Circle cx={200} cy={135} r={4} fill="none" stroke={DT.varColor} strokeWidth="1.4" />
      <Line x1={200} y1={100} x2={285} y2={100} stroke={DT.stroke} strokeWidth="2.4" />
      <Polygon points={`200,135 196,128 204,128`} fill={DT.varColor} />

      {/* Particle counter instrument */}
      <Rect x={285} y={80} width={55} height={50} rx="4" fill={DT.fill} stroke={DT.varColor} strokeWidth="1.6" />
      {/* LCD screen */}
      <Rect x={290} y={86} width={45} height={20} fill="#001020" stroke={DT.varColor} strokeWidth="0.6" />
      <SvgText x={312} y={95} fill="#6CFFB3" fontSize="6" fontFamily={DT.mono} textAnchor="middle">≥0.5μm</SvgText>
      <SvgText x={312} y={103} fill="#6CFFB3" fontSize="8" fontFamily={DT.mono} textAnchor="middle" fontWeight="700">3520</SvgText>
      <Circle cx={295} cy={118} r={2} fill={DT.varColor} />
      <Circle cx={305} cy={118} r={2} fill={DT.dim} />
      <Circle cx={315} cy={118} r={2} fill={DT.dim} />
      <SvgText x={312} y={128} fill={DT.desc} fontSize="6" fontFamily={DT.mono} textAnchor="middle">counter</SvgText>

      {/* ISO class reference strip */}
      <Rect x={20} y={225} width={310} height={20} fill="rgba(0,0,0,0.5)" stroke={DT.dim} strokeWidth="0.6" />
      {['ISO 5: 3520', 'ISO 6: 35200', 'ISO 7: 352000', 'ISO 8: 3.5M'].map((t, i) => (
        <SvgText key={i} x={55 + i * 75} y={238} fill={DT.leader} fontSize="7" fontFamily={DT.mono} textAnchor="middle">{t}</SvgText>
      ))}

      {/* Labels */}
      <LeaderLine x1={130} y1={145} x2={25} y2={70} varName="C_n" desc="particles/m³ at size n" anchor="start" />
      <LeaderLine x1={312} y1={80} x2={330} y2={55} varName="counter" desc="≥0.5 µm / 5 µm" anchor="end" />

      <FormulaTag formula="C_n ≤ 10^N · (0.1/D)^2.08    (ISO 14644-1 §4.2)" />
    </DiagramCanvas>
  );
}
